import { Lead, LeadChannel, LeadSource } from 'models/Leads';
import { Message, MessagingChannel } from 'models/inbox/Messages';
import useAppQuery from '../../../hooks/useAppQuery';
import API from '../../../services/API';
import { GetThreadAvailableMessagingChannelsResponseTO } from '../../../models/inbox/Threads';
import useAppUser from '../../../hooks/useAppUser';

function transformFilteredChannels(channels: MessagingChannel[]) {
  if (!channels) {
    return [MessagingChannel.EMAIL];
  }

  return Array.from(
    new Set([
      channels.includes(MessagingChannel.AIRBNB) ||
      channels.includes(MessagingChannel.BOOKING_COM) ||
      channels.includes(MessagingChannel.VRBO)
        ? MessagingChannel.AUTO
        : MessagingChannel.EMAIL,
      ...channels.filter(
        (channel) =>
          channel !== MessagingChannel.AIRBNB &&
          channel !== MessagingChannel.BOOKING_COM &&
          channel !== MessagingChannel.VRBO,
      ),
    ]),
  );
}
function isChannelMatchingLeadSource(
  channel: MessagingChannel,
  leadSource: LeadSource | undefined,
) {
  if (!leadSource) {
    return true;
  }

  return (
    (channel !== MessagingChannel.BOOKING_COM ||
      leadSource === LeadSource.DIRECT_BOOKINGDOTCOM) &&
    (channel !== MessagingChannel.AIRBNB ||
      leadSource === LeadSource.DIRECT_AIRBNB) &&
    (channel !== MessagingChannel.VRBO ||
      leadSource === LeadSource.DIRECT_HOMEAWAY)
  );
}

function getDefaultMessagingChannel(
  channels: MessagingChannel[],
  lead: Lead | undefined,
  messages: Message[] | undefined,
) {
  if (channels.length === 1) {
    return channels[0];
  }

  if (messages?.length) {
    const [latestMessage] = messages;
    const { channel } = latestMessage;

    if (
      channel === MessagingChannel.AIRBNB ||
      channel === MessagingChannel.BOOKING_COM ||
      channel === MessagingChannel.VRBO
    ) {
      return MessagingChannel.AUTO;
    }

    if (channels.includes(channel)) {
      return channel;
    }
  }

  if (lead?.channel) {
    const { channel } = lead.channel;

    if (
      channel === LeadChannel.AIRBNB ||
      channel === LeadChannel.BOOKING_COM ||
      channel === LeadChannel.VRBO
    ) {
      return MessagingChannel.AUTO;
    }

    return MessagingChannel.EMAIL;
  }

  if (channels.includes(MessagingChannel.AUTO)) {
    return MessagingChannel.AUTO;
  }
  return MessagingChannel.EMAIL;
}

const useAvailableMessagingChannels = ({
  threadUid,
  availableMessagingChannelsMap = {},
  lead,
  messages,
}: {
  threadUid: string;
  availableMessagingChannelsMap?: Record<string, MessagingChannel[]>;
  lead: Lead | undefined;
  messages?: Message[];
}) => {
  const { isEmployee } = useAppUser();

  const availableMessagingChannels =
    threadUid && availableMessagingChannelsMap[threadUid];

  const {
    data: fetchedAvailableMessagingChannels,
    isInitialLoading: isLoadingMessagingChannels,
  } = useAppQuery(
    ['availableMessagingChannels', threadUid],
    async () => {
      const response =
        await API.get<GetThreadAvailableMessagingChannelsResponseTO>(
          '/v3/available-messaging-channels',
          {
            params: {
              threadUid,
            },
          },
        );

      return response.data.availableMessagingChannels;
    },
    { enabled: isEmployee && !availableMessagingChannels && !!threadUid },
  );

  const filteredChannels = (
    availableMessagingChannels || fetchedAvailableMessagingChannels
  )?.filter((channel) => isChannelMatchingLeadSource(channel, lead?.source));

  // @ts-expect-error TS2345 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
  const channels = transformFilteredChannels(filteredChannels);

  const defaultMessagingChannel = getDefaultMessagingChannel(
    channels,
    lead,
    messages,
  );

  return {
    fetchedAvailableMessagingChannels,
    channels,
    defaultMessagingChannel,
    isLoadingMessagingChannels,
  };
};

export default useAvailableMessagingChannels;
